import produce from 'immer';
import type { AnyAction } from 'redux';
import { errorMessageFromResponse } from '../../helpers/errors';
import type { ProjectGroup } from '../../types';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import {
  CLEAR_SELECTED_PROJECT_GROUPS,
  DELETE_PROJECT_GROUPS,
  FETCH_ALL_PROJECTS_IN_RELEASE_GROUP,
  FETCH_ALL_PROJECT_GROUPS,
  FETCH_ONE_PROJECT_GROUP,
  POLL_RELEASE_START,
  POLL_RELEASE_STOP,
  SET_PROJECT_GROUP_DEPENDENCY_INFO_OPTIONS,
  SET_PROJECT_GROUP_REPORT_OPTIONS,
  TOGGLE_PROJECT_GROUP_SELECT,
  TOGGLE_SELECT_ALL_PROJECT_GROUPS,
} from './actions';
import type { ProjectGroupStore } from './store';

export const getInitialState = (): ProjectGroupStore => ({
  projectGroups: {},
  status: getInitialStatus(),
  selectedProjectGroupIds: {},
  sortMode: (new URLSearchParams(window.location.search)).get('sort') || '-status',
  release: {
    data: null,
    status: {
      loading: false,
      error: null,
    },
  },
  releaseScans: {
    data: [],
    status: {
      loading: false,
      error: null,
    },
  },
  releaseSummary: {
    data: {
      project_count: null,
      license_count: null,
      dependency_count: null,
      unresolved_licensing_issue_count: null,
      unresolved_security_issue_count: null,
      unresolved_quality_issue_count: null,
      isSteady: false,
    },
    status: {
      loading: false,
      error: null,
    },
  },
  selectedReleaseId: null,
  selectedScanId: null,
  reportOptions: {},
  dependencyInfoOptions: {},
  fetchingAllProjectsInGroupStatus: {
    loading: false,
    error: null,
  },
  pollRelease: [],
});

const initialState = getInitialState();

export default createReducer(initialState, {
  // fetchAll action
  [`${FETCH_ALL_PROJECT_GROUPS}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${FETCH_ALL_PROJECT_GROUPS}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    (action.payload.data || []).forEach((projectGroup: ProjectGroup) => {
      if (projectGroup?.id) {
        draftState.projectGroups[projectGroup.id] = projectGroup;
      }
    });
  }),
  [`${FETCH_ALL_PROJECT_GROUPS}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // fetchOne action
  [`${FETCH_ONE_PROJECT_GROUP}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${FETCH_ONE_PROJECT_GROUP}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    if (action.payload.data?.id) {
      draftState.projectGroups[action.payload.data.id] = action.payload.data;
    }

    draftState.selectedReleaseId = action.payload.data?.releases?.[0]?.id;
    draftState.selectedScanId = action.meta.scanId || (action.payload.data?.releases?.[0]?.scans?.[0]?.id ?? null);
  }),
  [`${FETCH_ONE_PROJECT_GROUP}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // delete action
  [`${DELETE_PROJECT_GROUPS}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${DELETE_PROJECT_GROUPS}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    action.meta.projectGroupIDList.forEach((pgId: number) => delete draftState.projectGroups[pgId]);
  }),
  [`${DELETE_PROJECT_GROUPS}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),
  FETCH_PROJECT_GROUP_RELEASE_PENDING: (state) => produce(state, draftState => {
    draftState.release.status.loading = true;
    draftState.release.status.error = null;
  }),
  FETCH_PROJECT_GROUP_RELEASE_FULFILLED: (state, action) => produce(state, draftState => {
    draftState.release.status.loading = false;
    draftState.release.data = action.payload.data;

    // If there's a group with a releases array that includes the release that was fetched, update that release object.
    const group = draftState.projectGroups[action.payload.data.projectGroupId];
    if (group) {
      const release = group.releases?.find(({ id }) => id === action.payload.data.id);
      if (release) {
        Object.assign(release, action.payload.data);
      }
    }
  }),
  FETCH_PROJECT_GROUP_RELEASE_REJECTED: (state, action) => produce(state, draftState => {
    draftState.release.status.loading = false;
    draftState.release.status.error = errorMessageFromResponse(action.payload);
  }),
  SELECT_RELEASE: (state, action) => produce(state, draftState => {
    draftState.selectedReleaseId = action.payload;
  }),

  [`${FETCH_ALL_PROJECTS_IN_RELEASE_GROUP}_PENDING`]: (state) => produce(state, draftState => {
    draftState.fetchingAllProjectsInGroupStatus.loading = true;
    draftState.fetchingAllProjectsInGroupStatus.error = null;
  }),
  [`${FETCH_ALL_PROJECTS_IN_RELEASE_GROUP}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.fetchingAllProjectsInGroupStatus.loading = false;
    const { groupId } = action.meta;
    if (draftState.projectGroups[groupId]) {
      draftState.projectGroups[groupId].allProjects = action.payload.data;
    }
  }),
  [`${FETCH_ALL_PROJECTS_IN_RELEASE_GROUP}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.fetchingAllProjectsInGroupStatus.loading = false;
    draftState.fetchingAllProjectsInGroupStatus.error = `Could not fetch all projects in release group: ${errorMessageFromResponse(action.payload)}`;
  }),

  SELECT_RELEASE_SCAN: (state, action) => produce(state, draftState => {
    draftState.selectedScanId = action.payload;
  }),
  [TOGGLE_PROJECT_GROUP_SELECT]: (state, action) => produce(state, draft => {
    action.payload.forEach((id: number) => {
      if (draft.selectedProjectGroupIds[id]) {
        delete draft.selectedProjectGroupIds[id];
      } else {
        draft.selectedProjectGroupIds[id] = true;
      }
    });
  }),

  [TOGGLE_SELECT_ALL_PROJECT_GROUPS]: (state: Readonly<ProjectGroupStore>, action: AnyAction) => produce(state, (draftState: ProjectGroupStore) => {
    //  The component will correctly know the next state because it is
    //  using both selected projects and release groups to determine this.
    if (action.isChecked) {
      draftState.selectedProjectGroupIds = Object.values(draftState.projectGroups)
        .map(projectGroup => projectGroup.id)
        .reduce((acc, projectGroupId) => ({ ...acc, [projectGroupId]: true }), {});
    } else {
      draftState.selectedProjectGroupIds = [];
    }
  }),

  [CLEAR_SELECTED_PROJECT_GROUPS]: (state) => ({ ...state, selectedProjectGroupIds: [] }),
  FETCH_PROJECT_GROUP_RELEASE_SUMMARY_PENDING: (state) => produce(state, draftState => {
    draftState.releaseSummary.status.loading = true;
  }),
  FETCH_PROJECT_GROUP_RELEASE_SUMMARY_FULFILLED: (state, action) => produce(state, draftState => {
    draftState.releaseSummary.status.loading = false;
    const { data } = action.payload;
    draftState.releaseSummary.data = data;
  }),
  FETCH_PROJECT_GROUP_RELEASE_SUMMARY_REJECTED: (state, action) => produce(state, draftState => {
    draftState.releaseSummary.status.loading = false;
    draftState.releaseSummary.status.error = errorMessageFromResponse(action.payload);
  }),
  FETCH_PROJECT_GROUP_RELEASE_SCANS_PENDING: (state) => produce(state, draftState => {
    draftState.releaseScans.status.loading = true;
  }),
  FETCH_PROJECT_GROUP_RELEASE_SCANS_FULFILLED: (state, action) => produce(state, draftState => {
    draftState.releaseScans.status.loading = false;
    const { data } = action.payload;
    draftState.releaseScans.data = data;
  }),
  FETCH_PROJECT_GROUP_RELEASE_SCANS_REJECTED: (state, action) => produce(state, draftState => {
    draftState.releaseScans.status.loading = false;
    draftState.releaseScans.status.error = errorMessageFromResponse(action.payload);
  }),
  SCAN_PROJECT_GROUP_RELEASE_PENDING: (state) => produce(state, draftState => {
    draftState.releaseScans.status.loading = true;
  }),
  SCAN_PROJECT_GROUP_RELEASE_FULFILLED: (state, action) => produce(state, draftState => {
    draftState.releaseScans.status.loading = false;
    const { data: { issues, releaseScan } } = action.payload;
    releaseScan.issues = issues;
    draftState.releaseScans.data.push(releaseScan);
  }),
  SCAN_PROJECT_GROUP_RELEASE_REJECTED: (state, action) => produce(state, draftState => {
    draftState.releaseScans.status.loading = false;
    draftState.releaseScans.status.error = errorMessageFromResponse(action.payload);
  }),
  [SET_PROJECT_GROUP_REPORT_OPTIONS]: (state, action) => produce(state, draftState => {
    const { projectGroup, options } = action.payload;
    const reportOptions = state.reportOptions[projectGroup];
    draftState.reportOptions[projectGroup] = {
      ...reportOptions,
      ...options,
    };
  }),
  [SET_PROJECT_GROUP_DEPENDENCY_INFO_OPTIONS]: (state, action) => produce(state, draftState => {
    const { projectGroup: locator, options = [] } = action.payload;
    draftState.dependencyInfoOptions[locator] = [
      ...options,
    ];
  }),
  [POLL_RELEASE_START]: (state, action) => produce(state, draftState => {
    const { projectGroupId, releaseId } = action.payload;
    draftState.pollRelease = [
      { projectGroupId, releaseId },
    ];
  }),
  [POLL_RELEASE_STOP]: (state) => produce(state, draftState => {
    draftState.pollRelease = [];
  }),
});
